import { lerp } from './maths'
import config from '../config';

export default class Background {

    static get settings() {
        return {
            COLOR_SCHEMES: {
                cyan: [
                    { r: 172, g: 252, b: 192},
                    { r: 153, g: 254, b: 255},
                    { r: 255, g: 235, b: 204}
                ],
                blue: [
                    { r: 147, g: 222, b: 255},
                    { r: 194, g: 159, b: 245},
                    { r: 132, g: 150, b: 255}
                ],
                orange: [
                    { r: 252, g: 202, b: 145},
                    { r: 255, g: 161, b: 222},
                    { r: 252, g: 229, b: 241}
                ],
                green: [
                    { r: 254, g: 255, b: 174},
                    { r: 164, g: 245, b: 185},
                    { r: 230, g: 255, b: 238}
                ],
                rose: [
                    { r: 255, g: 166, b: 219},
                    { r: 179, g: 202, b: 252},
                    { r: 246, g: 232, b: 255}
                ]
            },
            DEBUG: false,
        }
    }

    constructor($el, options) {

        const theme = options.theme || document.documentElement.getAttribute('data-theme')
        this.scheme = Background.settings.COLOR_SCHEMES[theme]
        this.debug = !!options.debug

        this.$el = $el
        this.$canvas = $el.querySelector('canvas')
        this.ctx = this.$canvas.getContext('2d');

        if(Background.settings.DEBUG) {
            this.gui = new dat.GUI();
            this.gui.domElement.parentNode.style.zIndex = 9999;
        }

        this.GRADIENTS_DATA = [
            {
                r: 0.4,
                stops: [
                    {
                        progress: 0,
                        color: this.scheme[1],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][1],
                        alpha: 0.66
                    },
                    {
                        progress: 0.8,
                        color: this.scheme[0],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][0],
                        alpha: 0.66
                    },
                    {
                        progress: 1,
                        color: this.scheme[0],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][0],
                        alpha: 0
                    }
                ]
            }, {
                r: 0.25,
                stops: [
                    {
                        progress: 0,
                        color: this.scheme[0],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][0],
                        alpha: 0.66
                    },
                    {
                        progress: 0.8,
                        color: this.scheme[1],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][1],
                        alpha: 0.66
                    },
                    {
                        progress: 1,
                        color: this.scheme[1],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][1],
                        alpha: 0
                    }
                ]
            },
            {
                r: 0.25,
                    stops: [
                    {
                        progress: 0.12,
                        color: this.scheme[2],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][2],
                        alpha: 0.66
                    },
                    {
                        progress: 1,
                        color: this.scheme[0],
                        defaultColor: Background.settings.COLOR_SCHEMES['rose'][0],
                        alpha: 0
                    }
                ]
            }
        ]

        this.compute();

        if(!config.IS_MOBILE) {
            this.manageMouse();
        }

        this.onResizeBind = this.onResize.bind(this)
        window.addEventListener('resize', this.onResizeBind);

        this.populate();
    }

    onResize() {
        if(!this.resizeTick) {
            this.resizeTick = true;

            requestAnimationFrame(() => {
                this.compute()
                this.populate()

                this.resizeTick = false
            })
        }
    }

    compute() {
        this.width = this.$el.offsetWidth;
        this.height = this.$el.offsetHeight;

        this.$canvas.width = this.width
        this.$canvas.height = this.height

        this.ctx.width = this.width;
        this.ctx.height = this.height;
    }

    manageMouse() {
        if(config.IS_MOBILE) return;
        this.mouseUsed = false
        this.mouse = { x: 0, y: 0 };

        this.bindMousemove = (e) => {
            // Update mouse position
            this.mouse.x = e.clientX;
            this.mouse.y = e.clientY;

            // ON FIRST MOUSE MOVE
            if(!this.mouseUsed) {
                // Ok we are using a mouse
                this.mouseUsed = true;

                // Create the gradient instance
                this.mouseGradient = new BackgroundItem({
                    ctx: this.ctx,
                    x: this.mouse.x,
                    y: this.mouse.y,
                    r: this.ctx.width/this.ctx.height > 1 ? this.ctx.width*0.1 : this.ctx.height*0.1,
                    lerpPosition: 0.1,
                    stops: [
                        {
                            progress: 0.15,
                            color: this.scheme[0],
                            defaultColor: Background.settings.COLOR_SCHEMES['rose'][0],
                            alpha: 0.5
                        },
                        {
                            progress: 1,
                            color: this.scheme[0],
                            defaultColor: Background.settings.COLOR_SCHEMES['rose'][0],
                            alpha: 0
                        }
                    ]
                })

                if(Background.settings.DEBUG) {
                    this.mouseGradient.setGUI(this.gui, 'Mouse Gradient')
                }
            }
        }
        window.addEventListener('mousemove',this.bindMousemove);
    }

    populate() {

        if(this.raf !== undefined) {
            cancelAnimationFrame(this.raf);
        }

        // Remove previous gradients if there are some
        if(this.gradients && this.gradients.length) {
            for(let gradient of this.gradients) gradient.destroy()
        }

        // Set a new array of gradients
        this.gradients = []

        let gradient
        this.GRADIENTS_DATA.forEach((g, i) => {
            gradient = new BackgroundItem({
                ctx: this.ctx,
                randomPosition: true,
                r: this.ctx.width/this.ctx.height > 1 ? this.ctx.width * g.r : this.ctx.height * g.r,
                stops: g.stops
            })
            gradient.animate()
            if(Background.settings.DEBUG) {
                gradient.setGUI(this.gui, `Gradient ${i + 1}`)
            }

            this.gradients.push(gradient)
        })

        this.draw();
    }

    // Drawing loop
    draw() {
        this.ctx.fillStyle = '#F9F9F9';
        this.ctx.fillRect(0,0,this.ctx.width,this.ctx.height);

        for(let gradient of this.gradients) {
            gradient.draw()
        }

        // Draw mouse gradient if needed
        if(this.mouseUsed && !config.IS_MOBILE) {
            this.mouseGradient.setPosition(this.mouse.x, this.mouse.y)
            this.mouseGradient.draw()
        }

        if(!config.LOW_PERFORMANCE) {
            this.raf = requestAnimationFrame(this.draw.bind(this));
        }
    }

    destroy() {
        cancelAnimationFrame(this.raf);
        window.removeEventListener('resize', this.onResizeBind);

        if(!config.IS_MOBILE) {
            window.removeEventListener('mousemove',this.bindMousemove);
        }

        if(this.gui && this.gui.destroy) {
            this.gui.destroy();
        }
    }
}


class BackgroundItem {

    static get settings() {
        return {
            PADDING_PERCENTAGE: 0.2,
            SCALE_RANGE: 0.1,
            DIRECTION_UPDATE_DELAY: 6,
            DEFAULT_LERP_FACTORS: {
                position: 0.005,
                scale: 0.01
            }
        }
    }

    constructor(options) {
        // Canvas context
        this.ctx = options.ctx

        // Position & scaling properties
        this.x = options.x
        this.y = options.y
        this.r = options.r

        // Check if we need to set a randomPosition
        this.randomPosition = options.randomPosition ? true : false
        if(this.randomPosition) {
            let {x,y} = this.getRandomPosition()
            this.x = x,
            this.y = y
        }

        // Setup color stops
        this.stops = JSON.parse(JSON.stringify(options.stops)); // JSON hack to prevent data-sync
        for(let stop of this.stops) {
            stop.originalColor = JSON.parse(JSON.stringify(stop.color)); // JSON hack to prevent data-sync
        }

        // Set default values for some properties
        this.scale = 1;
        this.expandScale = 1;
        this.isNeutralColors = false;

        // Set lerpfactors
        this.lerpFactors = {
            position: options.lerpPosition ? options.lerpPosition : BackgroundItem.settings.DEFAULT_LERP_FACTORS.position,
            scale: options.lerpScale ? options.lerpScale : BackgroundItem.settings.DEFAULT_LERP_FACTORS.scale
        }

        // This object is used to store lerped data (the data used to draw the gradients)
        this.drawPosition = {
            x: parseInt(this.x),
            y: parseInt(this.y),
            scale: parseFloat(this.scale)
        }
    }

    getRandomPosition() {
        return {
            x: (this.ctx.width * BackgroundItem.settings.PADDING_PERCENTAGE) + Math.random() * (this.ctx.width - (this.ctx.width * BackgroundItem.settings.PADDING_PERCENTAGE * 2)),
            y: (this.ctx.height * BackgroundItem.settings.PADDING_PERCENTAGE) + Math.random() * (this.ctx.height - (this.ctx.height * BackgroundItem.settings.PADDING_PERCENTAGE * 2))
        }
    }

    // Call this once and it will move the gradient to another point on a regular interval (see DIRECTION_UPDATE_DELAY)
    animate() {
        this.animating = true
        this.tw = gsap.to(this, {
            ...this.getRandomPosition(),
            scale: Math.random() * BackgroundItem.settings.SCALE_RANGE + (1-BackgroundItem.settings.SCALE_RANGE/2), // Random value around 1 with an amplitude of BackgroundItem.settings.SCALE_RANGE
            duration: BackgroundItem.settings.DIRECTION_UPDATE_DELAY,
            ease: 'linear',
            onComplete: () => {
                this.animate()
            }
        })
    }

    setPosition(x,y) {
        this.animating = true;
        this.x = x
        this.y = y
    }

    setGUI(guiInstance, label = 'Gradient') {

        let gradientFolder = guiInstance.addFolder(label)
        gradientFolder.add(this, 'debug')
        gradientFolder.add(this, 'x', 0, this.ctx.width)
        gradientFolder.add(this, 'y', 0, this.ctx.width)
        gradientFolder.add(this, 'r', 0, this.ctx.width)

        for(let i = 0; i < this.stops.length; i++) {
            let stopFolder = gradientFolder.addFolder('ColorStop '+i)
            stopFolder.add(this.stops[i], 'progress', 0, 1, 0.01);
            stopFolder.addColor(this.stops[i], 'color');
            stopFolder.add(this.stops[i], 'alpha', 0, 1, 0.01);
        }
    }

    draw() {
        try {
            // Store positioning values in local variables to alter them temporarily if needed
            let x = this.x
            let y = this.y
            let scale = this.scale

            // If animating, we need to lerp the data
            if(this.animating) {
                // Update drawPosition object
                this.drawPosition.x = lerp(this.drawPosition.x, this.x, this.lerpFactors.position);
                this.drawPosition.y = lerp(this.drawPosition.y, this.y, this.lerpFactors.position);
                this.drawPosition.scale = lerp(this.drawPosition.scale, this.scale, this.lerpFactors.scale);

                // Update local variables to match w/ drawPosition object
                x=this.drawPosition.x
                y=this.drawPosition.y
                scale=this.drawPosition.scale
            }

            // Create the gradient w/ its coords / scale
            let fillStyle = this.ctx.createRadialGradient(x,y,0,x,y,this.r * scale * this.expandScale)

            // Add all color stops
            for(let stop of this.stops) {
                fillStyle.addColorStop(stop.progress, `rgba(${stop.color.r}, ${stop.color.g}, ${stop.color.b}, ${stop.alpha})`)
            }
            // Actually draw the thing
            this.ctx.fillStyle = fillStyle;
            this.ctx.fillRect(0,0,this.ctx.width, this.ctx.height);

            // Draw DEBUG helpers
            if(Background.settings.DEBUG) {
                this.ctx.fillStyle = 'red';
                this.ctx.fillRect(this.x-1,this.y-1, 2, 2);

                this.ctx.strokeStyle = 'red';
                this.ctx.beginPath()
                this.ctx.arc(this.x,this.y,this.r * this.scale * this.expandScale,0,2*Math.PI)
                this.ctx.stroke()
            }
        } catch (error) {
            console.error(error)
        }
    }

    // DefaultColor being the color wanted when reaching the bottom CTA on most pages
    toggleDefaultColors(value) {
        if(value)
            this.isDefaultColors = value
        else
            this.isDefaultColors = !this.isDefaultColors

        if(this.colorsTw && this.colorsTw.kill) this.colorsTw.kill()
        this.colorsTw = gsap.timeline({});

        for(let stop of this.stops) {
            let targetColor = this.isDefaultColors ? stop.defaultColor : stop.originalColor;
            this.colorsTw.to(stop.color, {
                r: targetColor.r,
                g: targetColor.g,
                b: targetColor.b,
                duration: 1,
                ease: 'power2.Out'
            }, 0)
        }
    }

    expand() {
        if(this.expandTw && this.expandTw.kill) this.expandTw.kill()
        this.expandTw = gsap.to(this, { expandScale: 3, duration: 2, ease: 'power2.InOut' })

        this.toggleDefaultColors(true);
    }

    shrink() {
        if(this.expandTw && this.expandTw.kill) this.expandTw.kill()
        this.expandTw = gsap.to(this, { expandScale: 1, duration: 2, ease: 'power2.InOut' })

        this.toggleDefaultColors(false);
    }

    destroy() {
        if(this.expandTw && this.expandTw.kill) this.expandTw.kill()
        if(this.tw && this.tw.kill) this.tw.kill()
    }
}
